import { useCallback, useMemo, useState } from 'react';
import cx from 'classnames';

import {
  CustomSlate,
  useRichSlateEditor,
  // @ts-expect-error - no TypeScript definitions yet
} from 'src/components/RichSlate';
import { NAVIGATION_BAR_HEIGHT } from 'src/data/common';
import { queryProjectVariationDetails } from 'src/graphql/project';
import { copyHtmlAndTextToClipboard } from 'src/lib/clipboard';
import { isGraphQLAccessError } from 'src/lib/graphql';
import { getEditorCopyContent, htmlToSlate, SLATE_EMPTY_SELECTION_COPY } from 'src/lib/slate';
import type { VoidFunction } from 'src/lib/types';
// @ts-expect-error - no TypeScript definitions yet
import { useNotificationActions } from 'src/store/notification';
import {
  type FetchDocumentVariationDetails,
  ReadonlyDocumentScores,
  ReadonlyDocumentScoresStoreContainer,
  useReadonlyDocumentDetailsPanel,
  useReadonlyDocumentHoverHandlers,
} from 'src/views/ReadonlyDocumentScores';
import { SavedDocumentView } from 'src/views/SavedDocumentView';

import styles from './ReviewerDocument.module.scss';

interface ReviewerDocumentProps {
  className?: string;
  projectId: string;
  editorContent: string;
  onAccessLost: VoidFunction;
}

const Document = ({ className, projectId, editorContent, onAccessLost }: ReviewerDocumentProps) => {
  const editor = useRichSlateEditor();
  const [initialValue] = useState(() => htmlToSlate(editorContent));
  const [portalElement, setPortalElement] = useState<HTMLDivElement | null>(null);
  const notificationActions = useNotificationActions();
  const { onMouseMove, onMouseLeave } = useReadonlyDocumentHoverHandlers();
  const { detailsOpen, closeDetails } = useReadonlyDocumentDetailsPanel();

  const handleCopyClick = useCallback(() => {
    const { html, text } = getEditorCopyContent(editor, {
      emptySelection: SLATE_EMPTY_SELECTION_COPY.wholeContent,
    });
    copyHtmlAndTextToClipboard(html, text).then(() => {
      notificationActions.displaySuccess('Copied to clipboard');
    });
  }, [editor, notificationActions]);

  const fetchDetails = useCallback<FetchDocumentVariationDetails>(
    async (variation, text) => {
      try {
        return await queryProjectVariationDetails({
          projectId,
          assetGroupId: variation.assetGroupId,
          assetId: variation.id,
          text,
        });
      } catch (error) {
        if (isGraphQLAccessError(error as Error)) {
          onAccessLost();
          return null;
        }
        throw error;
      }
    },
    [projectId, onAccessLost]
  );

  const editableProps = useMemo(
    () => ({ className: styles.editable, onMouseMove, onMouseLeave }),
    [onMouseMove, onMouseLeave]
  );

  const renderScores = useCallback(
    (parentElement: HTMLElement) => (
      <ReadonlyDocumentScores
        className={styles.scores}
        parentElement={parentElement}
        portalElement={portalElement}
        benchmarkProjectId={projectId}
        fetchDetails={fetchDetails}
      />
    ),
    [portalElement, projectId, fetchDetails]
  );

  return (
    <div className={cx(styles.root, className)}>
      <CustomSlate
        editor={editor}
        initialValue={initialValue}
      >
        <SavedDocumentView
          projectId={projectId}
          readOnly
          coveredTop={NAVIGATION_BAR_HEIGHT}
          detailsOpen={detailsOpen}
          editableProps={editableProps}
          renderScores={renderScores}
          onCopyClick={handleCopyClick}
          onAccessLost={onAccessLost}
          onPanelOpen={closeDetails}
        />
      </CustomSlate>

      <div
        className={styles.detailsPortal}
        ref={setPortalElement}
      />
    </div>
  );
};

/** The project's saved document, read-only, with its scores, comments and ratings. */
export const ReviewerDocument = (props: ReviewerDocumentProps) => (
  <ReadonlyDocumentScoresStoreContainer>
    <Document {...props} />
  </ReadonlyDocumentScoresStoreContainer>
);
